import { Alert, Box, Breadcrumbs, LinearProgress, Stack, Typography } from "@mui/material";
import { Navigate, useNavigate, useParams } from "react-router";
import { useKeptWhileReloading } from "../../modules/dashboard/hooks/use-kept-while-reloading";
import { HeaderBar } from "../../modules/generic/components/header-bar";
import { Page } from "../../modules/layout/components/page";
import { NewProgramButton } from "../../modules/program-edit/components/program-drawer";
import { ProgramsLanding } from "../../modules/program-landing/components/programs-landing";
import { ProgramsSwitcher } from "../../modules/program-landing/components/programs-switcher";
import {
  ProgramSwitcher,
  programHref,
} from "../../modules/program-tree/components/program-switcher";
import { useProgramTree } from "../../modules/program-tree/hooks/use-program-tree";
import { usePermissions } from "../../server/auth/use-permissions";
import { isUUID } from "../../utils/uuid";
import NotFound from "../not-found";
import { ProgramPage } from "./program-page";

import type { FC, ReactNode } from "react";

/**
 * A page in the full page layout: a header bar with the breadcrumbs and the button that opens the
 * sidebar, and the page below it, which scrolls.
 */
const Frame: FC<{ title: string; crumbs: ReactNode; children: ReactNode }> = ({
  title,
  crumbs,
  children,
}) => (
  <Box sx={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0 }}>
    <HeaderBar navBurger>{crumbs}</HeaderBar>
    <Page title={title} sx={{ flex: 1, minHeight: 0, bgcolor: "background.default" }}>
      {children}
    </Page>
  </Box>
);

/** The column of the design: at most 1376px wide, in the centre, with 32px at each side. */
const Column: FC<{ children: ReactNode }> = ({ children }) => (
  <Box sx={{ px: 4, pt: 4, pb: 8 }}>
    <Stack spacing={3} sx={{ maxWidth: 1376, mx: "auto" }}>
      {children}
    </Stack>
  </Box>
);

/** The breadcrumbs at the top when the programs could not be loaded, so there is no switcher. */
const topCrumbs = (
  <Breadcrumbs aria-label="Breadcrumb">
    <Typography variant="body2" aria-current="page" sx={{ fontWeight: 600 }}>
      Programs
    </Typography>
  </Breadcrumbs>
);

/**
 * The programs page for the cha_4265_programs_2026 flag: the landing page at /tabbed-programs, and
 * one program at /tabbed-programs/:id, in the full page layout of ProgramsLayoutGate. A program the
 * caller cannot open is not found. An old link with the program's title in place of its id goes to
 * the id.
 */
const ProgramTreePage: FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // Keep the tree while it loads again after a program is changed, so the page stays as it is.
  const { data: tree, error, loading, reload } = useKeptWhileReloading(useProgramTree());
  const { super_admin, ce_admin } = usePermissions();
  const admin = super_admin || ce_admin;

  if (error) {
    return (
      <Frame title="Programs | ChangeEngine" crumbs={topCrumbs}>
        <Column>
          <Alert severity="error">The programs could not be loaded.</Alert>
        </Column>
      </Frame>
    );
  }

  if (!tree) return <LinearProgress />;

  if (id && !isUUID(id)) {
    const titled = [...tree.byId.values()].find((node) => node.accessible && node.title === id);
    return titled ? <Navigate replace to={programHref(titled.id)} /> : <NotFound />;
  }

  if (!id) {
    return (
      <Frame title="Programs | ChangeEngine" crumbs={<ProgramsSwitcher tree={tree} />}>
        <Column>
          <Stack
            direction="row"
            useFlexGap
            sx={{
              alignItems: "flex-end",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 2,
            }}
          >
            <Typography variant="h4" component="h1">
              Programs
            </Typography>
            {admin ? (
              <NewProgramButton
                tree={tree}
                onCreated={(created) => {
                  reload();
                  void navigate(programHref(created));
                }}
              />
            ) : null}
          </Stack>
          <ProgramsLanding tree={tree} showAccess={admin} onMoved={admin ? reload : undefined} />
        </Column>
      </Frame>
    );
  }

  const current = tree.byId.get(id);
  // A folder made a moment ago is not in the tree until the tree loads again.
  if (!current?.accessible) return loading ? <LinearProgress /> : <NotFound />;

  return (
    <Frame
      title={`${current.title} | ChangeEngine`}
      crumbs={<ProgramSwitcher tree={tree} current={current} />}
    >
      <ProgramPage tree={tree} current={current} admin={admin} onProgramChanged={reload} />
    </Frame>
  );
};

export default ProgramTreePage;
